import { memo, useRef } from 'react';
import { mergeObject, replaceKey } from '../../utils';

import {
  HighChartWrapper,
  defaultOptions,
  tooltipOptions,
  plotOptions,
  percentData,
  xAxisLabelFormatCreator,
  getSvgOfDashStyle
} from '@euroland/react-high-chart';

import { number } from '@euroland/format-number';
import { formatDate, getDate } from '@euroland/libs';
import { useTemplateSetting } from '../../hooks/useCompanySettings';
import { DATE_SCREEN_READER, TOOL_TEMPLATE } from '../../constant/common';
import { i18n } from "@euroland/libs";
import { getCurrencyLabel, getTickerPatternById } from '../../store/utils';
import { useSelector } from 'react-redux';
import { colorBlindModeSelector } from '../../store/slice/settings/selectors';
import Highcharts from './setup';
import { formatNumberByInstrument } from '../../utils/format-number';
import useInstructionHighChart from '../../hooks/useInstructionHighChart';
import useCurrencyName from '../../hooks/useCurrencyName';

/**
 * @typedef { import('../../..').IHistoricalPrices } IHistoricalPrices
 * @typedef { import('../../..').IInstrumentSetting } IInstrumentSetting
 * @typedef { import('../../..').IPeerSetting } IPeerSetting
 * @typedef {{series: IHistoricalPrices[]}} HighChartMultipleLineProps
 * @typedef { import('../../store/slice/trade').IInstrumentDataAndSettingSelectorReturn } IInstrumentDataAndSettingSelectorReturn
 * @typedef { import('../../..').CompanySetting } CompanySetting
 */

/**
 * @param {{
 * series: IHistoricalPrices[],
 * instrumentDataAndSetting: IInstrumentDataAndSettingSelectorReturn
 * }} param0
 */
const createOptions = ({ series, instrumentDataAndSetting, colorBlind, isQuadrata, getCurrencyName }) => (chartUtils) => {
  const options = defaultOptions(chartUtils)
  const normalizes = series.map(item => percentData(item.datas.map(item => ([new Date(item.date).getTime(), item.close]))))
  const xAxis = [
    mergeObject(options.xAxis, {
      type: 'datetime',
      labels: {
        formatter: xAxisLabelFormatCreator({ formatDate, getDate }),
      }
    })];
  const yAxis = [
    mergeObject(options.yAxis, {
      title: {
        text: i18n.translate('percent'),
      },
      labels: {
        formatter: function () {
          return replaceKey(i18n.translate('percentFormat'), [number(this.value * 100)])
        }
      }
    })
  ]

  const plotOptionsSpline = plotOptions.spline(chartUtils)

  if (colorBlind) {
    delete plotOptionsSpline.marker.symbol;
    delete plotOptionsSpline.marker.states.hover.symbol;
    // options.legend.enabled = true
    // options.legend.symbolWidth = 40
  }
  const result = mergeObject(options, {
    accessibility: {
      enabled: true,
      series: {
        pointDescriptionEnabledThreshold: false,
        // descriptionFormat: '{seriesDescription}{authorDescription}{axisDescription}'
      },
      point: {
        descriptionFormatter: (point) => {
          const points = point.series.chart.series.map(item => item.data[point.index]).filter(Boolean);

          return points.map(point => {
            const date = formatDate(point.x, DATE_SCREEN_READER);
            return i18n.translate(
              'chartMultipleLinePointLabelAccessibility',
              date,
              point.series.options.ariaName,
              Math.round(point.y * 10000) / 100,
              point.series.options.currencyName
            )
          }).join(', ')
        },
      }
    },
    xAxis,
    yAxis,
    tooltip: mergeObject(tooltipOptions(chartUtils), {
      formatter: function () {
        return `
          <div class="custom-tooltip">
            <div class="custom-tooltip__time">${formatDate(this.x, 'shortDate')}</div>
            ${this.points.map((item, i) => {
          const options = item.series.options
          const normalize = options.normalize
          const currency = options.currency
          const instrumentId = options.seriesData.instrumentId;
          return `
                  <div class="custom-tooltip__item">
                    ${colorBlind ?
              getSvgOfDashStyle({
                color: item.color,
                lineWidth: options.lineWidth,
                dashStyle: options.dashStyle
              }) :
              `<span class="custom-tooltip__color" style="background-color: ${item.color};"></span>`}
                    <span class="custom-tooltip__label">${item.series.name}</span>
                    <span class="custom-tooltip__value">${formatNumberByInstrument(normalize.dataIndex[item.x], instrumentId)}</span>
                    <span class="custom-tooltip__currency">${replaceKey(i18n.translate('currencyFormat'), [currency])}</span>
                  </div>
              `
        }).join('')}
          </div>
        `
      },
    }),

    plotOptions: {
      column: { tooltip: { enabled: false } },
      spline: plotOptionsSpline
    },
    series: normalizes.map((normalize, i) => {
      const seriesData = series[i]
      const instrumentId = seriesData.instrumentId
      const instrument = instrumentDataAndSetting[instrumentId];

      const ariaName = isQuadrata ? instrument.data.shareName : `${instrument.data.ticker} ${instrument.data.marketName}`;
      const currencyName = getCurrencyName(seriesData.currency);
      const result = {
        color: instrument.setting.color,
        type: 'spline',
        normalize,
        dashStyle: 'Solid',
        seriesData,
        name: isQuadrata ? instrument.data.shareName : replaceKey(i18n.translate('shareName'), [instrument.data.ticker, instrument.data.marketAbbreviation]),
        data: normalize.percentData,
        currency: getCurrencyLabel(seriesData.currency || instrument.data.currencyCode),
        currencyName,
        ariaName
      }

      if (colorBlind) {
        const pattern = getTickerPatternById(instrumentId)
        result.dashStyle = pattern
      }
      return result
    })
  })

  return result;
}

export const HighChartMultipleLineVictor = memo((/** @type { HighChartMultipleLineProps } */{ series, instrumentDataAndSetting }) => {
  const colorBlind = useSelector(colorBlindModeSelector)
  const chatContainerRef = useRef()
  useInstructionHighChart(chatContainerRef)
  const getCurrencyName = useCurrencyName()
  return (
    <HighChartWrapper
      className="highchart-multiple-line"
      createOptions={createOptions({ series, instrumentDataAndSetting, colorBlind, getCurrencyName })}
      highCharts={Highcharts}
      allowChartUpdate
      isRtl={window.appSettings.isRtl}
      immutable={true}
      ref={chatContainerRef}
    />
  )
}, (prev, next) => {
  return prev.series === next.series
})

const createOptionsQuadrata = (options) => (chartElement) => {
  const victorOption = createOptions(options)(chartElement)
  const { colorBlind } = options;
  victorOption.yAxis[0].labels.formatter = function () {
    return number(this.value)
  }
  victorOption.tooltip.formatter = function () {
    return ` <div class="custom-tooltip">
    <div class="custom-tooltip__time" aria-label="${formatDate(this.x, '', DATE_SCREEN_READER)}">${formatDate(this.x, 'shortDate')}</div>
    ${this.points.map((item, i) => {
      const options = item.series.options
      const normalize = options.normalize
      const currency = options.currency
      const instrumentId = options.seriesData.instrumentId;
      return `
        <div class="custom-tooltip__item">
          ${colorBlind ?
          getSvgOfDashStyle({
            color: item.color,
            lineWidth: options.lineWidth,
            dashStyle: options.dashStyle
          }) :
          `<span class="custom-tooltip__color" style="background-color: ${options.color};"></span>`
        }
          <span class="custom-tooltip__content">
            <span class="custom-tooltip__label">${item.series.name}</span>
            <span class="custom-tooltip__wrap">
                <span class="custom-tooltip__currency">${currency}</span>
                <span class="custom-tooltip__value">${formatNumberByInstrument(normalize.dataIndex[item.x], instrumentId)}</span>
            </span>
          </span>
        </div>
        `
    }).join('')}
    </div>`
  }
  return victorOption;
}

export const HighChartMultipleLineQuadrata = memo((/** @type { HighChartMultipleLineProps } */{ series, instrumentDataAndSetting }) => {
  const colorBlind = useSelector(colorBlindModeSelector)
  const chatContainerRef = useRef()
  useInstructionHighChart(chatContainerRef)
  const getCurrencyName = useCurrencyName()
  return (
    <HighChartWrapper
      className="highchart-multiple-line highchart-multiple-line__quadrata"
      createOptions={createOptionsQuadrata({ series, instrumentDataAndSetting, colorBlind, isQuadrata: true, getCurrencyName })}
      highCharts={Highcharts}
      isRtl={window.appSettings.isRtl}
      allowChartUpdate
      immutable={true}
      ref={chatContainerRef}
    />
  )
}, (prev, next) => {
  return prev.series === next.series
})

export const HighChartMultipleLine = (props) => {
  const template = useTemplateSetting();

  switch (template) {
    case TOOL_TEMPLATE.victor:
      return <HighChartMultipleLineVictor {...props} />;
    case TOOL_TEMPLATE.quadrata:
      return <HighChartMultipleLineQuadrata {...props} />;
    default:
      throw new Error(`Template: ${template} do not exist.`)
  }
}
